import { Mail, Radio } from 'lucide-react';
import {
  type Box,
  P,
  SLOPE,
  groundElbow,
  lerp,
  leftV,
  bottomV,
  topV,
  type Pt,
} from '@/components/iso/iso';
import type { IsoConnector, IsoMotion, IsoObjectSpec } from '@/components/iso/IsoSceneStage';
import { BrandTile, INK, McpChip, RouterPlate, TerminalTile } from '@/components/iso/parts';

/** The homepage integrations cast on the proven developers-hero slot
 *  geometry — only what ships (claims audit 2026-09-27): the live click
 *  stream, email forwarding, the MCP server and the REST API. No
 *  third-party connector (Zapier, Slack, GA4, Mailchimp) or outbound webhook
 *  exists, so none is drawn; the catalog on /integrations carries the
 *  webhooks roadmap date. */
const S = {
  plate: { tx: 583, ty: 210, w: 340, h: 300, depth: 26 },
  stream: { tx: 310, ty: 56, w: 168, h: 96, depth: 10 }, // top-left, drops in
  email: { tx: 880, ty: 60, w: 190, h: 120, depth: 12 }, // top-right, drops in
  mcp: { tx: 250, ty: 584, w: 122, h: 110, depth: 14 }, // below-left
  api: { tx: 672, ty: 630, w: 190, h: 120, depth: 12 }, // below-right
} satisfies Record<string, Box>;

const CONNECTORS: IsoConnector[] = (() => {
  const { plate, stream, email, mcp, api } = S;

  const stA: Pt = { x: bottomV(stream).x, y: bottomV(stream).y + stream.depth + 4 };
  const stB = lerp(topV(plate), leftV(plate), 0.5);
  const stMid: Pt = { x: stA.x, y: stB.y - SLOPE * Math.abs(stB.x - stA.x) };
  const dStream = `M ${P(stB)} L ${P(stMid)} L ${P(stA)}`;

  const emEnd: Pt = { x: bottomV(email).x, y: bottomV(email).y + email.depth + 4 };
  const emDrop: Pt = { x: emEnd.x, y: emEnd.y + 13 };
  const emStart: Pt = { x: 790, y: emDrop.y + SLOPE * (emDrop.x - 790) };
  const dEmail = `M ${P(emStart)} L ${P(emDrop)} L ${P(emEnd)}`;

  return [
    // out: clicks to your code, mail to your inbox…
    { id: 'int-stream', d: dStream, node: stA, pulse: true },
    { id: 'int-email', d: dEmail, node: emEnd, pulse: true },
    // …in from the builder stack.
    { id: 'int-mcp', d: groundElbow(topV(mcp), lerp(leftV(plate), bottomV(plate), 0.3)), node: topV(mcp), pulse: true },
    { id: 'int-api', d: groundElbow(topV(api), bottomV(plate)), node: topV(api), pulse: true },
  ];
})();

const OBJECTS: IsoObjectSpec[] = [
  {
    id: 'stream',
    element: (
      <BrandTile box={S.stream} name="Click stream">
        <Radio size={34} color={INK} strokeWidth={1.9} />
      </BrandTile>
    ),
    label: { x: 340, y: 30, text: 'LIVE CLICKS', lead: { y1: 38, y2: 50 } },
  },
  {
    id: 'email',
    element: (
      <BrandTile box={S.email} name="Your inbox">
        <Mail size={34} color={INK} strokeWidth={1.9} />
      </BrandTile>
    ),
    label: { x: 910, y: 34, text: 'EMAIL', lead: { y1: 40, y2: 52 } },
  },
  { id: 'plate', element: <RouterPlate box={S.plate} /> },
  { id: 'mcp', element: <McpChip box={S.mcp} />, label: { x: 255, y: 754, text: 'CLAUDE / MCP', lead: { y1: 722, y2: 736 } } },
  { id: 'api', element: <TerminalTile box={S.api} />, label: { x: 702, y: 836, text: 'REST API', lead: { y1: 805, y2: 818 } } },
];

const MOTION: IsoMotion = {
  plate: { depth: 0.5, rise: 60 },
  stream: { depth: 1.7, rise: -120 },
  email: { depth: 1.5, rise: -110 },
  mcp: { depth: 1.3, rise: 100 },
  api: { depth: 1.2, rise: 105 },
};

export const INTEGRATIONS_SCENE = {
  viewBox: '0 0 1160 860',
  coreId: 'plate',
  objects: OBJECTS,
  connectors: CONNECTORS,
  motion: MOTION,
  shadows: [
    { box: S.plate },
    { box: S.stream, rx: 9 },
    { box: S.email, rx: 9 },
    { box: S.mcp },
    { box: S.api, rx: 9 },
  ],
};
